// HR candidate drawer — "Documents" sub-tab (#3146): the Document Collection
// files a candidate uploaded during the chat-native hiring flow (Resume/CV, IC,
// etc.). Shows each step's checklist with received/pending status, view/download
// for received files, the CV parse state, and the step's deadline gate state.
//
// Data: GET /jobs/applications/{application_id}/flow/documents (BE ourteam-backend#3788).
import { useEffect, useRef, useState } from "react";
import {
    AlertTriangleIcon,
    CalendarClockIcon,
    CheckCircle2Icon,
    ClockIcon,
    DownloadIcon,
    FileIcon,
    FileTextIcon,
    FolderOpenIcon,
    Loader2Icon,
} from "lucide-react";
import { EmptyState } from "@component/empty-state";
import {
    getApplicationFlowDocuments,
    fetchFlowDocumentObjectUrl,
    FlowDocumentsForbiddenError,
} from "@service/flow-documents";
import type {
    CvParseState,
    FlowCustomDocument,
    FlowDeadlineState,
    FlowDocumentItem,
    FlowDocumentStep,
    FlowDocumentsResult,
} from "@type/flow-documents";
import { useSnackbar } from "@hook/use-snackbar";
import { parseFormat } from "@util/date";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";

interface Props {
    applicationId: string;
    /** Only fetch when the tab is actually shown. */
    isActive: boolean;
}

const CV_PARSE_LABEL: Record<CvParseState, { label: string; className: string }> = {
    parsed: { label: "CV parsed", className: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900" },
    failed: { label: "CV parse failed", className: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900" },
    unsupported_format: { label: "Unsupported format", className: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900" },
};

function DeadlineBanner({ state, due }: { state: FlowDeadlineState; due?: string }) {
    const dueText = due ? parseFormat(due, "D MMM YYYY, h:mm A") : "";
    const config: Record<FlowDeadlineState, { text: string; className: string }> = {
        armed: {
            text: dueText ? `Due ${dueText}` : "Deadline set",
            className: "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-300",
        },
        met: {
            text: dueText ? `Completed · was due ${dueText}` : "Completed on time",
            className: "bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-950/40 dark:border-emerald-900 dark:text-emerald-300",
        },
        missed: {
            text: dueText ? `Deadline missed — was due ${dueText}` : "Deadline missed",
            className: "bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-950/40 dark:border-rose-900 dark:text-rose-300",
        },
    };
    const c = config[state];
    if (!c) return null;
    return (
        <div className={cn("flex items-center gap-2 rounded-lg border px-3 py-2 text-sm", c.className)}>
            <CalendarClockIcon className="h-4 w-4 flex-shrink-0" />
            <span>{c.text}</span>
        </div>
    );
}

function DocumentRow({ item, askedIn }: { item: FlowDocumentItem; askedIn?: string | null }) {
    const snackbar = useSnackbar();
    const [opening, setOpening] = useState(false);
    const parse = item.cv_parse_state ? CV_PARSE_LABEL[item.cv_parse_state] : null;
    const isImage = (item.content_type || "").startsWith("image/");

    const openFile = async () => {
        if (!item.download_url || opening) return;
        setOpening(true);
        try {
            const url = await fetchFlowDocumentObjectUrl(item.download_url);
            window.open(url, "_blank", "noopener,noreferrer");
            // Free the blob once the new tab has had time to load it.
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (err) {
            catchErrorLog(`[flow-documents] download failed: ${err}`);
            snackbar.showError("Couldn't open the file. Please try again.");
        } finally {
            setOpening(false);
        }
    };

    return (
        <div className="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className={cn(
                "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg",
                item.received ? "bg-blue-50 dark:bg-blue-950/40" : "bg-gray-100 dark:bg-gray-800"
            )}>
                {isImage ? (
                    <FileIcon className={cn("h-4 w-4", item.received ? "text-blue-600" : "text-gray-400")} />
                ) : (
                    <FileTextIcon className={cn("h-4 w-4", item.received ? "text-blue-600" : "text-gray-400")} />
                )}
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.label}</span>
                    {item.required && (
                        <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Required</span>
                    )}
                    {item.received ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
                            <CheckCircle2Icon className="h-2.5 w-2.5" /> Received
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] font-medium text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
                            <ClockIcon className="h-2.5 w-2.5" /> Pending
                        </span>
                    )}
                    {parse && (
                        <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium", parse.className)}>
                            {parse.label}
                        </span>
                    )}
                </div>
                {item.received ? (
                    <p className="mt-0.5 truncate text-xs text-gray-500">
                        {item.filename || "File received"}
                        {item.source === "whatsapp" && " · via WhatsApp"}
                        {item.received_at && ` · ${parseFormat(item.received_at, "D MMM YYYY, h:mm A")}`}
                    </p>
                ) : (
                    <p className="mt-0.5 text-xs text-gray-400">Not uploaded yet</p>
                )}
                {askedIn && (
                    <p className="mt-0.5 truncate text-xs text-gray-400" title={askedIn}>
                        Asked in: “{askedIn}”
                    </p>
                )}
            </div>
            {item.received && item.download_url && (
                <button
                    type="button"
                    onClick={openFile}
                    disabled={opening}
                    className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-md border border-gray-200 dark:border-gray-700 px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:border-blue-400 hover:text-blue-600 transition-colors disabled:opacity-60"
                >
                    {opening ? <Loader2Icon className="h-3.5 w-3.5 animate-spin" /> : <DownloadIcon className="h-3.5 w-3.5" />}
                    View
                </button>
            )}
        </div>
    );
}

function DocumentStepBlock({ step }: { step: FlowDocumentStep }) {
    const items = step.items ?? [];
    const total = step.total ?? items.length;
    const received = step.received_count ?? items.filter((i) => i.received).length;
    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                {/* Always show the canonical step name, not the BE's per-flow
                    step_label (e.g. "Assessment + document pack"). */}
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Document Collection</h4>
                <span className="text-xs text-gray-500">{received} of {total} received</span>
            </div>
            {step.deadline?.state && <DeadlineBanner state={step.deadline.state} due={step.deadline.due} />}
            <div className="space-y-2">
                {items.map((item, idx) => (
                    <DocumentRow key={item.name || idx} item={item} />
                ))}
            </div>
        </div>
    );
}

/**
 * Ad-hoc chat-collected documents — outside any Document Collection step
 * (HR/Maya asked in the WhatsApp thread and the candidate sent a file).
 * Reuses DocumentRow; each row adds the chat message it was asked in.
 */
function CustomDocumentsBlock({ docs }: { docs: FlowCustomDocument[] }) {
    const received = docs.filter((d) => d.received).length;
    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Additional documents</h4>
                <span className="text-xs text-gray-500">{received} received</span>
            </div>
            <div className="space-y-2">
                {docs.map((d, idx) => (
                    <DocumentRow
                        key={d.name || idx}
                        askedIn={d.asked_in}
                        item={{
                            name: d.name,
                            label: d.label,
                            is_cv: d.is_cv,
                            required: !!d.required,
                            received: !!d.received,
                            source: d.source,
                            filename: d.filename ?? undefined,
                            content_type: d.content_type ?? undefined,
                            received_at: d.received_at ?? undefined,
                            download_url: d.download_url ?? undefined,
                        }}
                    />
                ))}
            </div>
        </div>
    );
}

export default function FlowDocumentsTabContent({ applicationId, isActive }: Props) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<"forbidden" | "error" | null>(null);
    const [result, setResult] = useState<FlowDocumentsResult | null>(null);
    // Fetch once per application while the drawer is open.
    const fetchedForRef = useRef<string | null>(null);

    useEffect(() => {
        if (!isActive || !applicationId) return;
        if (fetchedForRef.current === applicationId) return;
        fetchedForRef.current = applicationId;

        let cancelled = false;
        setLoading(true);
        setError(null);
        getApplicationFlowDocuments(applicationId)
            .then((data) => {
                if (cancelled) return;
                setResult(data);
            })
            .catch((err) => {
                if (cancelled) return;
                fetchedForRef.current = null; // allow a retry on re-open
                if (err instanceof FlowDocumentsForbiddenError) {
                    setError("forbidden");
                } else {
                    setError("error");
                    catchErrorLog(`[flow-documents] load failed: ${err}`);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [applicationId, isActive]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2Icon className="h-6 w-6 animate-spin text-blue-500" />
            </div>
        );
    }

    if (error === "forbidden") {
        return (
            <EmptyState
                icon={<FolderOpenIcon className="h-12 w-12 text-gray-300 dark:text-gray-600" />}
                title="No access to these documents"
                description="You can only view documents for candidates of your own company."
            />
        );
    }

    if (error === "error") {
        return (
            <EmptyState
                icon={<AlertTriangleIcon className="h-12 w-12 text-gray-300 dark:text-gray-600" />}
                title="Couldn't load documents"
                description="Something went wrong loading this candidate's documents. Try reopening the tab."
            />
        );
    }

    // The BE returns one entry per Document Collection step under `steps`; keep
    // only steps that actually have a checklist. `custom_documents` (ad-hoc
    // chat-collected files) render as their own block — a candidate can have
    // those even when no step has a checklist.
    const steps = (result?.steps ?? []).filter((s) => (s.items?.length ?? 0) > 0);
    const customDocs = result?.custom_documents ?? [];
    if (steps.length === 0 && customDocs.length === 0) {
        return (
            <EmptyState
                icon={<FolderOpenIcon className="h-12 w-12 text-gray-300 dark:text-gray-600" />}
                title="No documents to collect"
                description="This candidate's hiring flow has no Document Collection step, or nothing has been requested yet."
            />
        );
    }

    return (
        <div className="space-y-6">
            {steps.map((step) => (
                <DocumentStepBlock key={step.step_id} step={step} />
            ))}
            {customDocs.length > 0 && <CustomDocumentsBlock docs={customDocs} />}
        </div>
    );
}
